import { Link } from "react-router-dom";
import { Scales, X, ShoppingCart, ArrowRight } from "@phosphor-icons/react";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import StarRating from "@/components/StarRating";

const ROWS = [
  { key: "price", label: "Price", render: (i, fmt) => fmt(i.price) },
  { key: "rating", label: "Rating", render: (i) => <StarRating value={i.rating} /> },
  { key: "motor", label: "Motor", render: (i) => i.specs?.motor || "—" },
  { key: "battery", label: "Battery", render: (i) => i.specs?.battery || "—" },
  { key: "range", label: "Range", render: (i) => i.specs?.range || "—" },
  { key: "charging_time", label: "Charging Time", render: (i) => i.specs?.charging_time || "—" },
  { key: "frame_material", label: "Frame", render: (i) => i.specs?.frame_material || "—" },
  { key: "weight", label: "Weight", render: (i) => i.specs?.weight || "—" },
  { key: "brakes", label: "Brakes", render: (i) => i.specs?.brakes || "—" },
];

export default function Compare() {
  const { items, remove, clear } = useCompare();
  const { add } = useCart();
  const { fmt } = useCurrency();

  return (
    <>
      <SEO title="Compare Electric Bikes" description="Compare electric bikes side by side — motor, battery, range, weight and price." path="/compare" />
      <PageHero title="Compare electric bikes" subtitle="Put your shortlist head to head and find your perfect ride." image="https://images.unsplash.com/photo-1622598473264-81a98f1c7be5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" crumbs={[{ label: "Compare" }]} />

      <section className="container-px py-12">
        {items.length === 0 ? (
          <div className="rounded-3xl border border-border bg-card p-12 text-center">
            <Scales size={56} className="mx-auto text-muted-foreground/40" />
            <p className="mt-4 text-lg font-semibold">No bikes to compare yet</p>
            <p className="mt-1 text-muted-foreground">Add bikes using the compare icon on any product.</p>
            <Link to="/shop" className="btn-primary mt-6">Browse eBikes <ArrowRight size={18} /></Link>
          </div>
        ) : (
          <>
            <div className="flex justify-end"><button onClick={clear} className="text-sm text-muted-foreground hover:text-primary">Clear all</button></div>
            <div className="mt-4 overflow-x-auto rounded-3xl border border-border" data-testid="compare-table">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr>
                    <th className="w-40 p-4"></th>
                    {items.map((i) => (
                      <th key={i.slug} className="p-4 align-top">
                        <div className="relative rounded-2xl border border-border bg-card p-3">
                          <button onClick={() => remove(i.slug)} className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full hover:bg-muted" aria-label="Remove"><X size={15} /></button>
                          <img src={i.thumbnail} alt={i.name} className="aspect-[4/3] w-full rounded-xl object-cover" loading="lazy" />
                          <Link to={`/product/${i.slug}`} className="mt-3 block text-sm font-bold hover:text-primary">{i.name}</Link>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((r) => (
                    <tr key={r.key} className="border-t border-border">
                      <td className="bg-muted/50 p-4 text-sm font-bold">{r.label}</td>
                      {items.map((i) => <td key={i.slug} className="p-4 text-sm">{r.render(i, fmt)}</td>)}
                    </tr>
                  ))}
                  <tr className="border-t border-border">
                    <td className="bg-muted/50 p-4"></td>
                    {items.map((i) => (
                      <td key={i.slug} className="p-4">
                        <button onClick={() => add({ ...i, sale_price: null, price: i.price })} className="btn-primary w-full !py-2.5 text-sm"><ShoppingCart size={16} /> Add</button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  );
}
